import moment, { Moment } from 'moment';
import { ChangeEvent, useMemo, useState } from 'react';
import {
    DatePicker,
    DropDownSelect,
    Label,
    LabelWithRequired,
} from '@motili/ui';
import styled from 'styled-components';
import {
    DOCUMENT_UPLOAD_FIELDS,
    TAX_ID_TYPE_OPTIONS,
    TRADE_TYPE_OPTIONS,
    US_STATE_OPTIONS,
} from '../../../common/constants';
import { RequiredFormFields } from '../../../common/types';
import StyledTextInput from '../../../components/styled/StyledTextInput';
import { useForm } from 'react-hook-form';
import { getTaxIdPattern, TINType } from '../../../common/utils';

export interface FormFieldValues {
    effectiveDate?: string;
    expirationDate?: string;
    issueDate?: string;
    taxIdNumber?: string;
    taxIdType?: string;
    state?: string;
    tradeType?: string;
}

interface SelectOption {
    value: string;
    label: string;
}

interface Props {
    fileNameId: string;
    onChange?: (values: FormFieldValues) => void;
    /**
     * Fields listed here span the full width of the row (grid-column: 1 / -1)
     * instead of sharing a column with the other fields.
     */
    fullWidthFields?: RequiredFormFields[];
}

const FileUploadFormFields = ({
    fileNameId,
    onChange,
    fullWidthFields,
}: Props) => {
    const { requiredFormFields } =
        DOCUMENT_UPLOAD_FIELDS[
            fileNameId as keyof typeof DOCUMENT_UPLOAD_FIELDS
        ];

    const [effectiveDate, setEffectiveDate] = useState<Moment | null>(null);
    const [effectiveDateFocused, setEffectiveDateFocused] = useState(false);
    const [expirationDate, setExpirationDate] = useState<Moment | null>(null);
    const [expirationDateFocused, setExpirationDateFocused] = useState(false);
    const [dateRangeError, setDateRangeError] = useState(false);
    const [issueDateError, setIssueDateError] = useState(false);
    const [issueDate, setIssueDate] = useState<Moment | null>(null);
    const [issueDateFocused, setIssueDateFocused] = useState(false);
    const [taxIdNumber, setTaxIdNumber] = useState('');
    const [taxIdType, setTaxIdType] = useState<SelectOption | undefined>(
        undefined
    );
    const [state, setState] = useState<SelectOption | undefined>(undefined);
    const [tradeType, setTradeType] = useState<SelectOption | undefined>(
        undefined
    );

    const placeHolderText = useMemo(() => {
        if (!taxIdType) {
            return 'Select TIN type first';
        }
        return taxIdType.value === TINType.SSN ? 'NNN-NN-NNNN' : 'NN-NNNNNNN';
    }, [taxIdType]);

    const {
        register,
        formState: { errors },
    } = useForm({
        mode: 'all',
    });

    const taxIdReg = register('taxIdNumber', {
        required: true,
        pattern: {
            value: getTaxIdPattern(taxIdType?.value),
            message:
                taxIdType?.value === TINType.SSN
                    ? 'SSN must be in format NNN-NN-NNNN'
                    : 'EIN must be in format NN-NNNNNNN',
        },
    });

    const updateFormFields = (patch: Partial<FormFieldValues>) => {
        onChange?.({
            effectiveDate: effectiveDate?.toISOString(),
            expirationDate: expirationDate?.toISOString(),
            issueDate: issueDate?.toISOString(),
            taxIdNumber,
            taxIdType: taxIdType?.value,
            state: state?.value,
            tradeType: tradeType?.value,
            ...patch,
        });
    };

    const handleEffectiveDateChange = (date: moment.Moment | null) => {
        setEffectiveDate(date);
        const isInvalid =
            !!date && !!expirationDate && expirationDate.isSameOrBefore(date);
        setDateRangeError(isInvalid);
        updateFormFields({
            effectiveDate: date?.toISOString() ?? undefined,
            ...(isInvalid ? { expirationDate: undefined } : {}),
        });
    };

    const handleExpirationDateChange = (date: moment.Moment | null) => {
        setExpirationDate(date);
        const isInvalid =
            !!date && !!effectiveDate && date.isSameOrBefore(effectiveDate);
        setDateRangeError(isInvalid);
        updateFormFields({
            expirationDate: isInvalid
                ? undefined
                : (date?.toISOString() ?? undefined),
        });
    };

    const handleIssueDateChange = (date: moment.Moment | null) => {
        const _issueDateError = !!date && isDayAfterCurrentDay(date.valueOf());
        setIssueDate(date);
        setIssueDateError(_issueDateError);
        updateFormFields({
            issueDate: _issueDateError
                ? undefined
                : (date?.toISOString() ?? undefined),
        });
    };

    const isTaxIdValid = (value: string, tinType: string | undefined) => {
        const pattern = getTaxIdPattern(tinType);
        return !value || !pattern || pattern.test(value);
    };

    const handleTaxIdNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
        taxIdReg.onChange(event);
        const value = event.target.value;
        setTaxIdNumber(value);
        updateFormFields({
            taxIdNumber: isTaxIdValid(value, taxIdType?.value)
                ? value
                : undefined,
        });
    };

    const handleTaxIdTypeChange = (option: unknown) => {
        const selected = option as SelectOption;
        setTaxIdType(selected);
        updateFormFields({
            taxIdType: selected?.value,
            taxIdNumber: isTaxIdValid(taxIdNumber, selected?.value)
                ? taxIdNumber
                : undefined,
        });
    };

    const handleStateChange = (option: unknown) => {
        const selected = option as SelectOption;
        setState(selected);
        updateFormFields({ state: selected?.value });
    };

    const handleTradeTypeChange = (option: unknown) => {
        const selected = option as SelectOption;
        setTradeType(selected);
        updateFormFields({ tradeType: selected?.value });
    };

    const isDayAfterCurrentDay = (day: number) => {
        return moment().startOf('day').diff(day, 'days') < 0;
    };

    const renderFieldContent = (field: RequiredFormFields) => {
        switch (field) {
            case RequiredFormFields.effectiveDate:
                return (
                    <>
                        <LabelWithRequired required htmlFor='effectiveDate'>
                            EFFECTIVE DATE
                        </LabelWithRequired>
                        <DatePicker
                            id='effectiveDate'
                            date={effectiveDate}
                            onChange={handleEffectiveDateChange}
                            isDayBlocked={() => false}
                            focused={effectiveDateFocused}
                            setFocused={setEffectiveDateFocused}
                            numberOfMonths={1}
                            openDirection={'down'}
                            displayFormat={''}
                            borderColor={'#CCCCCC'}
                        />
                    </>
                );
            case RequiredFormFields.expirationDate:
                return (
                    <>
                        <LabelWithRequired required htmlFor='expirationDate'>
                            EXPIRATION DATE
                        </LabelWithRequired>
                        <DatePicker
                            id='expirationDate'
                            date={expirationDate}
                            onChange={handleExpirationDateChange}
                            isDayBlocked={() => false}
                            focused={expirationDateFocused}
                            setFocused={setExpirationDateFocused}
                            numberOfMonths={1}
                            openDirection={'down'}
                            displayFormat={''}
                            borderColor={'#CCCCCC'}
                        />
                    </>
                );
            case RequiredFormFields.issueDate:
                return (
                    <>
                        <Label htmlFor='issueDate'>ISSUE DATE</Label>
                        <DatePicker
                            id='issueDate'
                            date={issueDate}
                            onChange={handleIssueDateChange}
                            isDayBlocked={(day: number) =>
                                isDayAfterCurrentDay(day)
                            }
                            focused={issueDateFocused}
                            setFocused={setIssueDateFocused}
                            numberOfMonths={1}
                            openDirection={'down'}
                            displayFormat={''}
                            borderColor={'#CCCCCC'}
                        />
                    </>
                );
            case RequiredFormFields.taxIdNumber:
                return (
                    <StyledTextInput
                        label='TIN (SSN OR EIN)'
                        name='taxIdNumber'
                        value={taxIdNumber}
                        onChange={handleTaxIdNumberChange}
                        required
                        inputStyles={{
                            width: '100%',
                        }}
                        placeholder={placeHolderText}
                        disabled={!taxIdType}
                        register={taxIdReg}
                        error={!!errors.taxIdNumber}
                        errorMessage={errors.taxIdNumber?.message as string}
                    />
                );
            case RequiredFormFields.taxIdType:
                return (
                    <DropDownSelect
                        required
                        value={taxIdType}
                        closeMenuOnSelect={false}
                        onChange={handleTaxIdTypeChange}
                        name='taxIdType'
                        label='TIN TYPE (SSN OR EIN)'
                        options={TAX_ID_TYPE_OPTIONS}
                    />
                );
            case RequiredFormFields.state:
                return (
                    <DropDownSelect
                        required
                        value={state}
                        closeMenuOnSelect={false}
                        onChange={handleStateChange}
                        name='state'
                        label='STATE'
                        options={US_STATE_OPTIONS}
                    />
                );
            case RequiredFormFields.tradeType:
                return (
                    <DropDownSelect
                        required
                        value={tradeType}
                        closeMenuOnSelect={false}
                        onChange={handleTradeTypeChange}
                        name='trade'
                        label='TRADE'
                        options={TRADE_TYPE_OPTIONS}
                    />
                );
            default:
                return null;
        }
    };

    const renderRequiredFormFields = () => {
        return requiredFormFields.map(field => (
            <StyledFieldWrapper
                key={field}
                $fullWidth={!!fullWidthFields?.includes(field)}
            >
                {renderFieldContent(field)}
            </StyledFieldWrapper>
        ));
    };

    return (
        <>
            <StyledFormFieldContainer>
                {renderRequiredFormFields()}
            </StyledFormFieldContainer>
            {dateRangeError && (
                <StyledError>
                    Expiration date cannot be before the effective date.
                </StyledError>
            )}
            {issueDateError && (
                <StyledError>Issue date cannot be in the future.</StyledError>
            )}
        </>
    );
};

const StyledFormFieldContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 24px;
`;

const StyledFieldWrapper = styled.div<{ $fullWidth: boolean }>`
    display: flex;
    flex-direction: column;
    flex: ${({ $fullWidth }) => ($fullWidth ? '1 1 100%' : '1 1 0')};
`;

const StyledError = styled.p`
    color: ${({ theme }) => theme.colors.red};
    margin-top: 4px;
`;

export default FileUploadFormFields;
